import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const EMPTY = { nama: "", peran: "", bio: "", foto: "", file: null, preview: null };

export default function AdminTim() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/team").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({ nama: m.nama, peran: m.peran, bio: m.bio, foto: m.foto || "", file: null, preview: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let foto = form.foto || null;
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        foto = up.data.url;
      }
      const payload = { nama: form.nama, peran: form.peran, bio: form.bio, foto };
      if (editId) {
        await api.put(`/team/${editId}`, payload);
        toast.success("Data anggota diperbarui.");
      } else {
        await api.post("/team", payload);
        toast.success("Anggota baru ditambahkan.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ylbh_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan data anggota.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (m) => {
    if (!window.confirm(`Hapus ${m.nama} dari tim? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Anggota dihapus.");
      load();
    } catch {
      toast.error("Gagal menghapus anggota.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminShell title="Kelola Tim">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl text-[#F5F5F5]">Tim</h1>
        <button
          onClick={openCreate}
          data-testid="tim-baru-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light"
        >
          <Plus size={15} /> Tambah Anggota
        </button>
      </div>

      {form && (
        <form
          data-testid="tim-form"
          onSubmit={submit}
          className="mt-8 space-y-5 border border-gold/30 bg-[#0A0A0A] p-6 lg:p-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-[#F5F5F5]">
              {editId ? "Ubah Anggota" : "Anggota Baru"}
            </h2>
            <button type="button" onClick={() => setForm(null)} aria-label="Tutup formulir" data-testid="tim-form-close">
              <X size={20} className="text-[#737373] hover:text-white" />
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Nama Lengkap + Gelar *</label>
              <input data-testid="tim-nama-input" required minLength={2} value={form.nama} onChange={set("nama")} className={INPUT_CLASS} placeholder="Nama lengkap, S.H" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Jabatan *</label>
              <input data-testid="tim-peran-input" required minLength={2} value={form.peran} onChange={set("peran")} className={INPUT_CLASS} placeholder="Advokat / Paralegal / …" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Bio Singkat *</label>
              <textarea data-testid="tim-bio-textarea" required minLength={10} rows={3} value={form.bio} onChange={set("bio")} className={`${INPUT_CLASS} resize-y`} placeholder="Deskripsi singkat peran dan pengalaman anggota" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                Foto — unggah dari komputer (JPG/PNG/WebP, maks 5 MB)
              </label>
              <div className="flex flex-wrap items-center gap-5">
                <label
                  data-testid="tim-upload-label"
                  className="flex cursor-pointer items-center gap-2 border border-dashed border-gold/50 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus size={15} /> Pilih Foto
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="tim-file-input"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setForm((prev) => ({ ...prev, file: f, preview: URL.createObjectURL(f) }));
                    }}
                  />
                </label>
                {(form.preview || form.foto) && (
                  <img
                    src={form.preview || imgSrc(form.foto)}
                    alt="Pratinjau foto anggota"
                    data-testid="tim-preview-image"
                    className="h-20 w-16 border border-white/10 object-cover"
                  />
                )}
                {!form.preview && !form.foto && (
                  <span className="text-xs font-light text-[#737373]">
                    Tanpa foto, kartu memakai monogram inisial nama.
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            data-testid="tim-simpan-button"
            disabled={saving}
            className="bg-gold px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light disabled:opacity-60"
          >
            {saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Tambahkan"}
          </button>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {items === null ? (
          <p className="py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : (
          items.map((m) => (
            <div
              key={m.id}
              data-testid={`tim-row-${m.id}`}
              className="flex flex-wrap items-center gap-x-5 gap-y-3 border border-white/10 bg-[#0d0d0d] px-5 py-4"
            >
              {m.foto ? (
                <img src={imgSrc(m.foto)} alt={m.nama} className="h-12 w-10 border border-white/10 object-cover grayscale" />
              ) : (
                <span className="flex h-12 w-10 items-center justify-center border border-white/10 bg-[#101010] font-serif text-sm italic text-gold/50">
                  {m.inisial}
                </span>
              )}
              <span className="font-serif text-lg text-[#F5F5F5]">{m.nama}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-gold">{m.peran}</span>
              <span className="ml-auto flex gap-2">
                <button
                  onClick={() => openEdit(m)}
                  data-testid={`tim-edit-${m.id}`}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                >
                  <Pencil size={12} /> Ubah
                </button>
                <button
                  onClick={() => hapus(m)}
                  data-testid={`tim-hapus-${m.id}`}
                  className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-red-300/80 transition-colors hover:border-red-400 hover:text-red-300"
                >
                  <Trash2 size={12} /> Hapus
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
